import { useEffect, useRef, useState } from 'react';
import type { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { PillTabs, Tabs } from '../Ant';
import { ChevronDown } from '../icons';
import {
  applyStripOffset,
  clampStripOffset,
  readTabStripMetrics,
  resolveTabCenter,
} from './tabStripOffset';
import { useScrollableTabStrip } from './useScrollableTabStrip';

export type CustomTabsVariant = 'underline' | 'pill';

interface CustomTabsProps extends TabsProps {
  testId?: string;
  items?: (NonNullable<TabsProps['items']>[number] & { testId?: string })[];
  className?: string;
  variant?: CustomTabsVariant;
}

const tabsByVariant: Record<CustomTabsVariant, typeof Tabs> = {
  underline: Tabs,
  pill: PillTabs,
};

/** How long the offset keeps being recomputed after a tab change while the layout settles, in ms. */
const STRIP_SETTLE_MS = 350;

/**
 * The gutter that lines the pill chips up with the card below, rendered as leading extra content
 * rather than as padding on the strip. rc-tabs subtracts extra content from the width it measures,
 * so the gutter reads to it as the strip's starting edge — which is what keeps the tabs it lists
 * as hidden, and so the contents of the overflow menu, matching what is actually off screen.
 */
const PILL_STRIP_GUTTER = { left: <span className="custom-tabs-strip-gutter" /> };

/** antd's pane cross-fade draws the leaving pane over the arriving one, which reads as a flicker under pill chips. */
const animationByVariant: Record<CustomTabsVariant, TabsProps['animated']> = {
  underline: { inkBar: true, tabPane: true },
  pill: { inkBar: true, tabPane: false },
};

const CustomTabs: React.FC<CustomTabsProps> = (props) => {
  const { testId, items, className, variant = 'underline', ...rest } = props;
  const { t } = useTranslation();
  const TabsVariant = tabsByVariant[variant];
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const [activeTabKey, setActiveTabKey] = useState<string | undefined>(
    props.activeKey as string | undefined,
  );

  useEffect(() => {
    if (props.activeKey !== activeTabKey) {
      setActiveTabKey(props.activeKey as string | undefined);
    }
  }, [props.activeKey, activeTabKey]);

  const scrollActiveTabIntoView = () => {
    const root = tabsRef.current;
    if (!root) return;

    const activeTab = root.querySelector<HTMLElement>('.ant-tabs-tab-active');
    const metrics = readTabStripMetrics(root);
    if (!activeTab || !metrics) return;

    const { tabList, visibleWidth, furthestOffset } = metrics;
    const centred = visibleWidth / 2 - resolveTabCenter(tabList, activeTab);

    applyStripOffset(tabList, clampStripOffset(centred, furthestOffset));
  };

  /**
   * A tab change swaps the pane underneath, and the widths the offset is derived from keep moving
   * while that content lands. Recompute every frame until they stop; the offset is only written
   * when it actually changes, so a settled strip is left alone mid-glide.
   */
  useEffect(() => {
    let frame = 0;
    const settleUntil = performance.now() + STRIP_SETTLE_MS;

    const reassertOffset = () => {
      scrollActiveTabIntoView();
      if (performance.now() < settleUntil) {
        frame = requestAnimationFrame(reassertOffset);
      }
    };

    reassertOffset();
    return () => cancelAnimationFrame(frame);
  }, [activeTabKey]);

  useEffect(() => {
    const onResize = () => scrollActiveTabIntoView();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useScrollableTabStrip(tabsRef);

  const handleTabClick = (key: string) => {
    setActiveTabKey(key);
    if (props.onChange) props.onChange(key);
  };

  return (
    <div
      ref={tabsRef}
      className={`${className || ''} custom-tabs custom-tabs-${variant} w-full h-full`}
      data-testid={testId}
    >
      <TabsVariant
        {...rest}
        activeKey={activeTabKey}
        onChange={handleTabClick}
        moreIcon={
          // antd gives the overflow button no name of its own, so it comes from the icon.
          <span role="img" aria-label={t('common:showMore')}>
            <ChevronDown />
          </span>
        }
        animated={animationByVariant[variant]}
        tabBarGutter={0}
        tabBarExtraContent={variant === 'pill' ? PILL_STRIP_GUTTER : undefined}
        items={items?.map(({ icon, ...item }) => ({
          ...item,
          label: (
            <span
              className="custom-tab-label"
              /* the styles reserve the active chip's heavier width from this; see the pill rule */
              data-label={typeof item.label === 'string' ? item.label : undefined}
              data-testid={`${item.testId || item.key}-tab`}
            >
              {item.label}
            </span>
          ),
          children: (
            <div className="h-full" data-testid={`${item.testId || item.key}-content`}>
              {item.children}
            </div>
          ),
        }))}
      />
    </div>
  );
};

export default CustomTabs;
